import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { showDesktopNotice } from './desktop-notifications';

/**
 * The pop-up that says something just happened.
 *
 * Driven by the bell's own feed rather than by a second channel: a notification is already a row
 * this session polls for, and anything that arrives unread and was not there a moment ago is by
 * definition news. Building a separate delivery path for the same fact would be two things to keep
 * in step.
 *
 * Nothing pops up on the first poll. Opening the product should not throw eight notifications from
 * yesterday at somebody — the bell has a count for that, and a toast is for what happened while you
 * were looking at something else.
 *
 * Toasts dismiss themselves and stack at most three deep. A pile of them is a modal nobody agreed
 * to, and the fourth one is never the one that mattered.
 *
 * The same arrivals also raise a **desktop notification** when the tab is not in front of somebody
 * (Phase 52). That is the case a toast cannot cover and the one a reminder is for: "reunião em 10
 * minutos" has to reach you in the document you are actually writing. It is silent unless the
 * person turned it on in their preferences, and silent while they are looking at this page — the
 * toast below already said it.
 */

const DISMISS_MS = 8000;
const MAX_VISIBLE = 3;

/** The shape the bell's feed already has; kept structural so the two cannot drift apart. */
export interface ToastSource {
  id: string;
  kind: string;
  message_key: string;
  values: Record<string, unknown>;
  read: boolean;
  link: string | null;
  /** What it is about, so a screen can act on it rather than only read it (Phase 125). */
  subject_type?: string | null;
  subject_id?: string | null;
}

/**
 * A meeting about to start is not a toast (Phase 125).
 *
 * It has a deadline and a set of things somebody wants to do about it — postpone the alert, push
 * the meeting, call it off — so it raises the reminder dialog instead, and must not also fade past
 * in the corner. Exported so the two places that decide this read the same rule.
 */
export function isEventReminder(notification: {
  kind: string;
  subject_type?: string | null;
  subject_id?: string | null;
}): boolean {
  return (
    notification.kind === 'DUE' &&
    notification.subject_type === 'calendar_event' &&
    Boolean(notification.subject_id)
  );
}

export interface ToastItem {
  id: string;
  kind: string;
  text: string;
  link: string | null;
}

/**
 * Turns a feed into the toasts that are new since the last look.
 *
 * Exported for its test: "what is new" is the whole behaviour, and it is the part that gets subtly
 * wrong — showing everything on the first load, or showing the same thing twice after a refetch.
 */
export function newArrivals(
  current: { id: string; read: boolean }[],
  seen: Set<string> | null,
): string[] {
  // `null` means this is the first look: everything is old news, whatever its read state.
  if (seen === null) return [];

  return current.filter((item) => !item.read && !seen.has(item.id)).map((item) => item.id);
}

export function Toasts({
  notifications,
  loaded,
  render,
}: {
  notifications: ToastSource[];
  /**
   * Whether `notifications` is a real feed yet. Before the first answer the list is empty, and
   * treating that emptiness as "seen" made every unread item a new arrival the moment the feed
   * came in — the same toasts on every page load.
   */
  loaded: boolean;
  /** How a notification reads, which the bell already knows how to do. */
  render: (notification: ToastSource) => string;
}): React.JSX.Element | null {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // What was on screen last time. `null` until the first feed arrives, which is what stops the
  // product greeting somebody with yesterday's backlog.
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!loaded) return;

    const arrived = newArrivals(notifications, seen.current);

    seen.current = new Set(notifications.map((notification) => notification.id));

    if (arrived.length === 0) return;

    const fresh = notifications
      .filter((notification) => arrived.includes(notification.id))
      // A meeting reminder gets the dialog, not a toast that fades while somebody reads it.
      .filter((notification) => !isEventReminder(notification))
      .map((notification) => ({
        id: notification.id,
        kind: notification.kind,
        text: render(notification),
        link: notification.link,
      }));

    if (fresh.length === 0) return;

    setToasts((current) => [...fresh, ...current].slice(0, MAX_VISIBLE));

    /*
     * The same news, for somebody who is not here.
     *
     * `showDesktopNotice` decides whether to show anything — it checks the permission and the tab's
     * visibility — so this loop stays a plain "tell it what happened" and the rules live in one
     * place rather than being half here and half there.
     */
    for (const notice of fresh) {
      showDesktopNotice({ id: notice.id, title: notice.text, link: notice.link }, (link) =>
        navigate(link),
      );
    }
  }, [notifications, loaded, render, navigate]);

  useEffect(() => {
    if (toasts.length === 0) return;

    const timer = setTimeout(() => setToasts((current) => current.slice(0, -1)), DISMISS_MS);

    return () => clearTimeout(timer);
  }, [toasts]);

  if (toasts.length === 0) return null;

  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.kind.toLowerCase()}`}>
          <span className="toast__text">{toast.text}</span>

          <span className="toast__actions">
            {toast.link && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => {
                  setToasts((current) => current.filter((each) => each.id !== toast.id));
                  navigate(toast.link!);
                }}
              >
                {t('toast.open')}
              </button>
            )}

            <button
              type="button"
              className="toast__close"
              aria-label={t('toast.dismiss')}
              onClick={() => setToasts((current) => current.filter((each) => each.id !== toast.id))}
            >
              ×
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
